import React, { useState } from "react";
import { Modal, Button, Carousel } from "react-bootstrap";

const ProjectDetailsModal = ({ showModal, setShowModal, projectDetails }) => {
    const [activeIndex, setActiveIndex] = useState(0);

    // Function to convert YouTube URL to embed URL
    const getYouTubeEmbedUrl = (url) => {
        if (!url) return null;
        
        console.log('Processing URL:', url); // Debug log
        
        // Handle different YouTube URL formats
        let videoId = null;
        
        if (url.includes('youtu.be/')) {
            // Short URL format: https://youtu.be/VIDEO_ID?params
            const urlParts = url.split('youtu.be/')[1];
            videoId = urlParts.split('?')[0].split('&')[0];
        } else if (url.includes('youtube.com/watch?v=')) {
            // Standard URL format: https://www.youtube.com/watch?v=VIDEO_ID&params
            const urlParams = new URLSearchParams(url.split('?')[1]);
            videoId = urlParams.get('v');
        } else if (url.includes('youtube.com/embed/')) {
            // Already embed format
            return url;
        }
        
        if (videoId) {
            const embedUrl = `https://www.youtube.com/embed/${videoId}`;
            console.log('Generated embed URL:', embedUrl); // Debug log
            return embedUrl;
        }
        
        console.log('Could not extract video ID from:', url); // Debug log
        return null;
    };

    // Function to check if URL is a YouTube video
    const isYouTubeVideo = (url) => {
        const isYT = url && (url.includes('youtube.com') || url.includes('youtu.be'));
        console.log('Is YouTube video:', url, isYT); // Debug log
        return isYT;
    };

    // Combine images and video into a single media array
    const mediaItems = [];
    
    // Add images to media array
    if (projectDetails?.images?.length > 0) {
        projectDetails.images.forEach((image, index) => {
            mediaItems.push({
                type: 'image',
                url: image.image_url,
                isPrimary: image.is_primary,
                id: image.id,
                alt: `Project image ${index + 1}`
            });
        });
    }
    
    // Add video to media array
    if (projectDetails?.video) {
        const videoUrl = projectDetails.video;
        const embedUrl = getYouTubeEmbedUrl(videoUrl);
        const isYT = isYouTubeVideo(videoUrl);
        
        console.log('Video processing:', { videoUrl, embedUrl, isYT }); // Debug log
        
        mediaItems.push({
            type: 'video',
            url: videoUrl,
            embedUrl: embedUrl,
            isYouTube: isYT,
            isPrimary: false,
            id: 'video',
            alt: 'Project video'
        });
    }

    const handleSelect = (selectedIndex) => {
        setActiveIndex(selectedIndex);
    };

    return (
        <Modal
            show={showModal}
            onHide={() => setShowModal(false)}
            size="xl"
            centered
            className="project-modal"
        >
            <Modal.Header closeButton className="border-bottom">
                <Modal.Title className="h5 text-dark fw-normal">
                    {projectDetails?.name || "Project Details"}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body className="p-0" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                {projectDetails ? (
                    <div className="container-fluid">
                        {/* Hero Section */}
                        <div className="row g-0">
                            <div className="col-12 bg-light py-4 px-4">
                                <div className="row align-items-center">
                                    <div className="col-auto">
                                        {projectDetails.logo_url && (
                                            <img
                                                src={projectDetails.logo_url}
                                                alt={projectDetails.name}
                                                className="rounded"
                                                style={{ width: '180px', objectFit: 'cover' }}
                                            />
                                        )}
                                    </div>
                                    <div className="col">
                                        <h4 className="mb-1 text-dark">{projectDetails.name}</h4>
                                        <p className="text-muted mb-2">{projectDetails.tagline}</p>
                                        <div className="d-flex gap-3 small text-muted">
                                            <span>ID: #{projectDetails.id}</span>
                                            <span>Votes: {projectDetails.votes_count}</span>
                                            {projectDetails.is_approved && (
                                                <span className="text-success">✓ Approved</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        {projectDetails.url && (
                                            <a
                                                href={projectDetails.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-primary btn-sm"
                                            >
                                                Visit Project
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Media Slider Section */}
                        {mediaItems.length > 0 && (
                            <div className="row g-0">
                                <div className="col-12 p-4 border-bottom">
                                    <h6 className="text-dark mb-3">
                                        Media 
                                        <span className="text-muted small ms-2">
                                            ({activeIndex + 1} of {mediaItems.length})
                                        </span>
                                    </h6>
                                     
                                    <Carousel 
                                        activeIndex={activeIndex} 
                                        onSelect={handleSelect}
                                        interval={null}   // Disable auto-scroll
                                        indicators={true}
                                        controls={mediaItems.length > 1}
                                        className="media-carousel"
                                        style={{ maxHeight: '400px' }}
                                    >
                                        {mediaItems.map((item, index) => (
                                            <Carousel.Item key={item.id} className="text-center">
                                                <div 
                                                    className="d-flex align-items-center justify-content-center position-relative"
                                                    style={{ height: '350px', backgroundColor: '#f8f9fa' }}
                                                >
                                                    {item.type === 'image' ? (
                                                        <>
                                                            <img
                                                                src={item.url}
                                                                alt={item.alt}
                                                                className="img-fluid rounded"
                                                                style={{ 
                                                                    maxHeight: '100%', 
                                                                    maxWidth: '100%',
                                                                    objectFit: 'contain'
                                                                }}
                                                            />
                                                            {item.isPrimary && (
                                                                <span className="badge bg-warning position-absolute top-0 end-0 m-3">
                                                                    Primary
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <div className="ratio ratio-16x9 rounded overflow-hidden" style={{ maxWidth: '80%' }}>
                                                            {item.isYouTube && item.embedUrl ? (
                                                                <iframe
                                                                    src={item.embedUrl}
                                                                    title="YouTube video player"
                                                                    frameBorder="0"
                                                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                                                    allowFullScreen
                                                                    style={{ width: "100%", height: "100%" }}
                                                                />
                                                            ) : (
                                                                // Fallback for non-YouTube videos or when embed URL fails
                                                                <div className="d-flex align-items-center justify-content-center h-100 bg-light text-muted">
                                                                    <div className="text-center">
                                                                        <div className="mb-2">
                                                                            <i className="fas fa-play-circle" style={{ fontSize: '3rem' }}></i>
                                                                        </div>
                                                                        <p className="mb-2">Video not available for embed</p>
                                                                        <a 
                                                                            href={item.url} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className="btn btn-primary btn-sm"
                                                                        >
                                                                            Watch on YouTube
                                                                        </a>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </Carousel.Item>
                                        ))}
                                    </Carousel>
                                    
                                    {/* Thumbnail Navigation */}
                                    {mediaItems.length > 1 && (
                                        <div className="d-flex justify-content-center mt-3 gap-2 flex-wrap">
                                            {mediaItems.map((item, index) => (
                                                <button
                                                    key={item.id}
                                                    className={`btn btn-sm p-1 border ${
                                                        activeIndex === index ? 'border-primary' : 'border-light'
                                                    }`}
                                                    onClick={() => setActiveIndex(index)}
                                                    style={{ width: '60px', height: '45px' }}
                                                >
                                                    {item.type === 'image' ? (
                                                        <img
                                                            src={item.url}
                                                            alt={`Thumbnail ${index + 1}`}
                                                            className="img-fluid rounded"
                                                            style={{ 
                                                                width: '100%', 
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    ) : (
                                                      <div 
                                                        className="d-flex align-items-center justify-content-center h-100 rounded"
                                                        style={{ 
                                                        fontSize: "14px", 
                                                        backgroundColor: "#FF0000",   
                                                        color: "#fff",                
                                                        width: "45px", 
                                                        height: "30px" 
                                                        }}
                                                        >
                                                        <span>▶</span>
                                                        </div>

                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Main Content */}
                        <div className="row g-0">
                            <div className="col-lg-8 p-4 border-end">
                                {/* Description */}
                                <div className="mb-4">
                                    <h6 className="text-dark mb-2">Description</h6>
                                    <div
                                        className="text-muted lh-base mb-0"
                                        dangerouslySetInnerHTML={{ __html: projectDetails.description }}
                                    />
                                </div>

                                {/* Features */}
                                {projectDetails.features && (
                                    <div className="mb-4">
                                        <h6 className="text-dark mb-2">Features</h6>
                                        <div
                                            className="text-muted lh-base mb-0"
                                            dangerouslySetInnerHTML={{ __html: projectDetails.features }}
                                        />
                                    </div>
                                )}

                                {/* Benefits */}
                                {projectDetails.benefits && (
                                    <div className="mb-4">
                                        <h6 className="text-dark mb-2">Benefits</h6>
                                        <div
                                            className="text-muted lh-base mb-0"
                                            dangerouslySetInnerHTML={{ __html: projectDetails.benefits }}
                                        />
                                    </div>
                                )}

                                {/* Categories */}
                                {projectDetails.categories?.length > 0 && (
                                    <div className="mb-4">
                                        <h6 className="text-dark mb-2">Categories</h6>
                                        <div className="d-flex flex-wrap gap-2">
                                            {projectDetails.categories.map((cat) => (
                                                <span
                                                    key={cat.id}
                                                    className="badge bg-light text-dark border rounded-pill px-3 py-1"
                                                    style={{ fontSize: '0.8rem' }}
                                                >
                                                    {cat.name}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="col-lg-4 p-4">
                                {/* Project Stats */}
                                <div className="mb-4">
                                    <h6 className="text-dark mb-3">Project Stats</h6>
                                    <div className="row g-3">
                                        <div className="col-6">
                                            <div className="text-center p-3 bg-light rounded">
                                                <div className="h4 mb-1 text-dark">{projectDetails.votes_count}</div>
                                                <small className="text-muted">Votes</small>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="text-center p-3 bg-light rounded">
                                                <div className="h4 mb-1 text-dark">#{projectDetails.id}</div>
                                                <small className="text-muted">Project ID</small>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Tech Stack */}
                                {projectDetails.tech_stack && (
                                    <div className="mb-4">
                                        <h6 className="text-dark mb-2">Tech Stack</h6>
                                        <p className="text-muted small mb-0">
                                            {projectDetails.tech_stack}
                                        </p>
                                    </div>
                                )}

                                {/* Collaborators */}
                                {projectDetails.collaborators && (
                                    <div className="mb-4">
                                        <h6 className="text-dark mb-2">Collaborators</h6>
                                        <p className="text-muted small mb-0">
                                            {projectDetails.collaborators}
                                        </p>
                                    </div>
                                )}

                                {/* Timeline */}
                                <div className="mb-4">
                                    <h6 className="text-dark mb-2">Timeline</h6>
                                    <div className="small text-muted">
                                        <div className="mb-1">
                                            <strong>Created:</strong> {new Date(projectDetails.created_at).toLocaleDateString()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Creator Section */}
                        {projectDetails.user && (
                            <div className="row g-0">
                                <div className="col-12 p-4 bg-light border-top">
                                    <div className="row align-items-center">
                                        <div className="col-auto">
                                            <div
                                                className="bg-primary text-white rounded d-flex align-items-center justify-content-center"
                                                style={{ width: '48px', height: '48px', fontSize: '18px' }}
                                            >
                                                {projectDetails.user.name.charAt(0).toUpperCase()}
                                            </div>
                                        </div>
                                        <div className="col">
                                            <h6 className="mb-1 text-dark">Created by</h6>
                                            <p className="mb-1">
                                                <strong>{projectDetails.user.name}</strong>
                                            </p>
                                            <p className="text-muted small mb-0">{projectDetails.user.email}</p>
                                        </div>
                                        <div className="col-auto">
                                            <small className="text-muted">
                                                Member since {new Date(projectDetails.user.created_at).getFullYear()}
                                            </small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading project details...</p>
                    </div>
                )}
            </Modal.Body>

            <Modal.Footer className="border-top bg-white">
                <div className="w-100 d-flex justify-content-between align-items-center">
                    <div className="text-muted small">
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-secondary"
                            onClick={() => setShowModal(false)}
                            className="px-4"
                        >
                            Close
                        </Button>
                        {projectDetails?.url && (
                            <Button
                                variant="primary"
                                onClick={() => window.open(projectDetails.url, '_blank')}
                                className="px-4"
                            >
                                Visit Project
                            </Button>
                        )}
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    )
}

export default ProjectDetailsModal